import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { hideSovereignFeatureFlags, SovereignFeatureKeys } from "../Common/Environment";

export interface CopilotBubbleConfig {
    enabled: boolean;
    preferredAgent?: string;
    starterQuestions?: string[];
    dismissable?: boolean;
}

interface CopilotBubbleContextValue {
    config: CopilotBubbleConfig;
    setConfig: React.Dispatch<React.SetStateAction<CopilotBubbleConfig>>;
}

const defaultConfig: CopilotBubbleConfig = { enabled: false };

const CopilotBubbleContext = createContext<CopilotBubbleContextValue>({
    config: defaultConfig,
    setConfig: () => undefined,
});

export const useCopilotBubble = (): CopilotBubbleContextValue => {
    return useContext(CopilotBubbleContext);
};

/** Sets copilot bubble config on mount and resets to disabled on unmount. */
export const useCopilotConfig = (config: CopilotBubbleConfig) => {
    const { setConfig } = useCopilotBubble();
    const { preferredAgent, starterQuestions, dismissable } = config;
    // Disable copilot in unavailable environments
    const enabled = config.enabled && !hideSovereignFeatureFlags[SovereignFeatureKeys.Copilot];
    useEffect(() => {
        setConfig({ enabled, preferredAgent, starterQuestions, dismissable });
        return () => setConfig(defaultConfig);
    }, [enabled, preferredAgent, starterQuestions, dismissable, setConfig]);
};

export const CopilotBubbleProvider = ({ children }: { children: React.ReactNode }) => {
    const [config, setConfig] = useState<CopilotBubbleConfig>(defaultConfig);

    const value = useMemo(() => ({ config, setConfig }), [config]);

    return (
        <CopilotBubbleContext.Provider value={value}>
            {children}
        </CopilotBubbleContext.Provider>
    );
};
